import {
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';

export type GetLsuvsForRowFn<TModel, TRowMetadata> = (
  model: TModel,
  row: StructuredTableRow<TRowMetadata>,
  columns: Array<StructuredTableColumn>
) => Array<LastSyncedUpstreamValue>;

/**
 * Extracts the last synced upstream values (LSUVs) for a procedure config table. Note that the
 * most significant work is delegated to the table adapter; here we just collate the LSUVs
 * by row.
 *
 * @param rows The table rows. These must have delegates already installed, or delegated columns
 *             will always report "stale"
 * @param columns
 * @param dataModels The underlying data models (e.g., material inputs) which were used to build the table
 * @param extractId Callback to retrieve the unique ID for this data model. Used as a key for the LSUVs
 * @param getLsuvsForRow Typically provided by the table adapter, this does the heavy lifting of building
 *                       an array of LSUVs for a data model
 */
export default function computeLSUVs<TModel, TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>,
  columns: Array<StructuredTableColumn>,
  dataModels: Array<TModel>,
  extractId: (model: TModel) => string | null,
  getLsuvsForRow: GetLsuvsForRowFn<TModel, TRowMetadata>
): Record<string, Array<LastSyncedUpstreamValue>> {
  const lsuvs: Record<string, Array<LastSyncedUpstreamValue>> = {};
  for (let i = 0; i < dataModels.length; ++i) {
    const row = rows[i];
    const model = dataModels[i];
    const id = model ? extractId(model) : null;
    if (id != null) {
      lsuvs[id] = getLsuvsForRow(model, row, columns);
    }
  }
  return lsuvs;
}
